import { HEADINGS, REPORTED_USER_TABLE } from "@/utils/constants/appConstants/commonConstants";
import ReportedUserEnvelopeTable from "@/pages/reportedUsers/ReportedUserEnvelopeTable";
import { useParams } from "react-router-dom";
import { useState, useEffect } from "react";
import { isFeaturePermitted } from "@/utils/functions/rbac";
import { contactUser, getUserById, markAsSafe, suspendUser } from "@/services/user.service";
import { UserDetailsSkeleton } from "@/components/skeletons";
import { errorToasterMessage, successToasterMessage } from "@/utils/functions/toaster";
import { SUCCESS_MESSAGES } from "@/utils/constants/appConstants/commonConstants";
import SuspendUserModal from "@/components/modals/SuspendUserModal";
import ContactModal from "@/components/modals/ContactModal";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { ChevronDown } from "lucide-react";

const ReportedUserDetail = () => {
    const { id } = useParams();
    const [isLoading, setIsLoading] = useState(false);
    const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
    const [showSuspendModal, setShowSuspendModal] = useState(false);
    const [statusLoading, setStatusLoading] = useState(false);
    const [contactLoading, setContactLoading] = useState(false);
    const [showContactModal, setShowContactModal] = useState(false);
    const [user, setUser] = useState<any>(null);
    const [isAdminPortalSuspentionPermitted, setIsAdminPortalSuspentionPermitted] = useState(false);
    const [isAdminPortalUpdateReportedUserPermitted, setIsAdminPortalUpdateReportedUserPermitted] = useState(false);

    useEffect(() => {
        if (!id) return;
        setIsLoading(true);
        getUserById(id, (response: any) => {
            setUser(response.data);
            setIsLoading(false);
        }, (error: any) => {
            setIsLoading(false);
        });
    }, [id]);

    useEffect(() => {
        setIsAdminPortalSuspentionPermitted(isFeaturePermitted("AdminPortalSuspendUser"));
        setIsAdminPortalUpdateReportedUserPermitted(isFeaturePermitted("AdminPortalUpdateReportedUser"));
    }, []);

    const handleSuspendClick = (userId: string) => {
        setSelectedUserId(userId);
        setShowSuspendModal(true);
    };

    const handleMarkAsSafe = (userId: string) => {
        setStatusLoading(true);
        markAsSafe(
            userId,
            (response: any) => {
                successToasterMessage(SUCCESS_MESSAGES.USER_MARKED_SAFE);
                setStatusLoading(false);
            },
            (err: any) => {
                errorToasterMessage(err.response.data.message);
                setStatusLoading(false);
            }
        );
    };

    const suspendUserhandler = (reason: string) => {
        if (!selectedUserId) return;
        setStatusLoading(true);
        suspendUser(
            { user_id: selectedUserId, reason: reason },
            (response: any) => {
                successToasterMessage(SUCCESS_MESSAGES.USER_SUSPENDED);
                setStatusLoading(false);
                setShowSuspendModal(false);
            },
            (err: any) => {
                errorToasterMessage(err.response.data.message);
                setStatusLoading(false);
                setShowSuspendModal(false);
            }
        );
    };

    const handleContactUser = (userId: string) => {
        setSelectedUserId(userId);
        setShowContactModal(true);
    };

    const handleContactSubmit = async (subject: string, body: string) => {
        if (!selectedUserId) return;
        setContactLoading(true);
        try {
            await contactUser(
                selectedUserId,
                { subject: subject, message: body },
                (response: any) => {
                    successToasterMessage(REPORTED_USER_TABLE.MESSAGE_SENT_SUCCESS);
                    setShowContactModal(false);
                    setContactLoading(false);
                },
                (err: any) => {
                    errorToasterMessage(err.response.data.message);
                    setContactLoading(false);
                }
            );
        } catch (error) {
            setContactLoading(false);
        }
    };

    return (
        isLoading ? <UserDetailsSkeleton /> :
        <div>
            <div className="flex items-start justify-between gap-3 mb-4">
                <div>
                    <h3 className="text-xl font-normal leading-snug capitalize text-secondary" id='reported-user-detail-title'>{user?.first_name} {user?.last_name}</h3>
                    <p className="text-sm text-muted-foreground" id='reported-user-detail-email'>{user?.email_address}</p>
                </div>
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button variant="default" size="sm" className="gap-1 bg-primary hover:bg-primary-hover-alt text-white" id="reported-user-detail-actions-btn">
                            {REPORTED_USER_TABLE.ACTIONS}
                            <ChevronDown className="h-4 w-4" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                        {isAdminPortalSuspentionPermitted && (
                            <DropdownMenuItem
                                onClick={() => handleSuspendClick(user.id)}
                                disabled={user?.status === 'suspended'}
                                id="reported-user-detail-suspend-btn"
                            >
                                <em className="icon ni ni-na mr-2" />
                                {REPORTED_USER_TABLE.SUSPEND_USER}
                            </DropdownMenuItem>
                        )}
                        <DropdownMenuItem onClick={() => handleContactUser(user.id)} id="reported-user-detail-contact-btn">
                            <em className="icon ni ni-mail mr-2" />
                            {REPORTED_USER_TABLE.CONTACT_USER}
                        </DropdownMenuItem>
                        {isAdminPortalUpdateReportedUserPermitted && (
                            <DropdownMenuItem onClick={() => handleMarkAsSafe(user.id)} id="reported-user-detail-mark-safe-btn">
                                <em className="icon ni ni-flag mr-2" />
                                {REPORTED_USER_TABLE.MARK_AS_SAFE}
                            </DropdownMenuItem>
                        )}
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>
            <ul className="gs-page-tabs">
                <li>
                    <button className="gs-page-tab gs-page-tab--active" id='reported-user-detail-envelope-tab'>
                        {HEADINGS.ENVELOPES}
                    </button>
                </li>
            </ul>
            <div>
                <ReportedUserEnvelopeTable id={id || ""} />
            </div>
            <SuspendUserModal show={showSuspendModal} onHide={() => setShowSuspendModal(false)} onSuspend={suspendUserhandler} loading={statusLoading} />
            <ContactModal
                show={showContactModal}
                onHide={() => setShowContactModal(false)}
                onContact={handleContactSubmit}
                loading={contactLoading}
            />
        </div>
    );
};

export default ReportedUserDetail;
